import { autoinject, bindable, Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ListFieldTyped } from 'models/ListSettings';
import {
  CommentForEntity,
  isStrapArticleType,
  MooringArticleSearchResult,
  MooringBridle,
  MooringCategoryType,
  MooringComponent,
  MooringComponentType,
} from 'models/mooring';
import { MooringArticleService, ToastService } from 'services';
import { MooringComponentSearchHandler, MooringOfferService, MooringService } from 'services/mooring';
import { toKg, toChecked } from 'lib/ui/ValueDisplayFormatters';
import { MooringTableBase } from '../table-base';
import { TableEntryManager } from 'services/ui-helpers';
import { DialogService } from 'aurelia-dialog';
import { PubSub } from 'lib/event/PubSub';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { AsyncErrorHandler } from 'lib/ui';
import { TableManager } from '../TableManager';
import { calculateFactorsMbl } from '../factors';
import {
  MooringBridleCalculatorDialog,
  MooringBridleCalculatorDialogArgs,
  MooringBridleCalculatorDialogResult,
} from 'features/mooring/mooring-bridle-calculator/mooring-bridle-calculator-dialog';
import { MooringSelectEntriesForPupulateArgs, SelectionOption } from 'features/mooring/component-templates/mooring-select-entries-for-populate';
import { SearchResult } from 'elements/search/search-input';
import { BridleCage, BridleCageDialog, BridleCageDialogArgs, BridleCageDialogResult } from './bridle-cage-dialog';
import { ensureNumberOrDefault } from 'lib/helpers';
import { FilterManager } from 'lib/tables/FilterManager';
import { Filters, filterTypes } from 'elements/Filter';
import { MaterialFactorService } from 'services/material-factor-service';
import { MaterialFactor } from 'models/mooring/MaterialFactor';
import { OfferViewContext } from '../../OfferViewContext';
import { CalculateUtilizationFactorDto, MooringStep } from 'models/mooring/UtilizationFactor';
import { MooringComponentSearchResult } from 'services/mooring/mooring-component-search-handler';

@autoinject
export class BridlesTable extends MooringTableBase<MooringBridle> {
  protected table = new TableManager('bridles-table', tableColumns);
  private filtersInitialized = false;

  @bindable
  protected onSave!: (args: { entries: MooringBridle[]; canNavigateOnSave: boolean }) => void;

  protected context = OfferViewContext.useContext(this.container);

  constructor(
    private container: Container,
    protected t: I18N,
    protected toast: ToastService,
    protected fm: FilterManager,
    dialogService: DialogService,
    mooringArticleService: MooringArticleService,
    // Used in HTML for article search
    mooringComponentSearchHandler: MooringComponentSearchHandler,
    tableEntryManager: TableEntryManager,
    pubsub: PubSub,
    confirmation: ConfirmDialog,
    mooringOfferService: MooringOfferService,
    mooringService: MooringService,
    materialFactorService: MaterialFactorService,
    materialFactors: MaterialFactor
  ) {
    super(
      'bridle',
      dialogService,
      mooringComponentSearchHandler,
      tableEntryManager,
      mooringArticleService,
      pubsub,
      confirmation,
      mooringOfferService,
      mooringService,
      materialFactorService,
      materialFactors
    );

    pubsub.sub('tabs:change-guard', async () => {
      if (this.changeTracker.areEqual(this.entries)) {
        this.pubsub.publish('tabs:change-guard-ok', null);
        return;
      }

      const res = await this.confirmation.confirmSave();

      if (res == 'cancel') {
        return;
      } else if (res === 'discard-continue') {
        this.pubsub.publish('tabs:change-guard-ok', null);
      } else {
        this.onSave({ entries: this.entries, canNavigateOnSave: true });
      }
    });
  }

  async attached() {
    await super.attached();
    if (!this.filtersInitialized) {
      this.initFilters([]);
    }
  }

  entriesChanged(newValue: MooringBridle[]) {
    super.entriesChanged(newValue);
    if (!newValue?.length || this.filtersInitialized) return;

    this.filtersInitialized = true;
    this.initFilters(newValue);
  }

  private initFilters(entries: MooringBridle[]) {
    const seenBoys = new Set();
    const buoys = entries
      .filter((x) => {
        if (seenBoys.has(x.BuoyId)) return false;
        seenBoys.add(x.BuoyId);
        return true;
      })
      .map((x) => ({ Id: x.BuoyId, Name: x.BuoyNo }))
      .sort((a, b) => {
        const letterA = a.Name.match(/[A-Za-z]+/)[0];
        const letterB = b.Name.match(/[A-Za-z]+/)[0];

        if (letterA !== letterB) {
          return letterA.localeCompare(letterB);
        }

        const numA = parseInt(a.Name.match(/\d+/)[0], 10);
        const numB = parseInt(b.Name.match(/\d+/)[0], 10);

        return numA - numB;
      });

    const seenCagess = new Set();
    const cages = entries
      .filter((x) => {
        if (seenCagess.has(x.CageId)) return false;
        seenCagess.add(x.CageId);
        return true;
      })
      .map((x) => ({ Id: x.CageId, Name: x.CageNo }))
      .sort((a, b) => {
        const letterA = a.Name.match(/[A-Za-z]+/)[0];
        const letterB = b.Name.match(/[A-Za-z]+/)[0];

        if (letterA !== letterB) {
          return letterA.localeCompare(letterB);
        }

        const numA = parseInt(a.Name.match(/\d+/)[0], 10);
        const numB = parseInt(b.Name.match(/\d+/)[0], 10);

        return numA - numB;
      });

    this.fm.init({
      context: 'mooring-bridles',
      storageKey: 'mooring-bridles',
      filters: {
        buoyIds: {
          name: 'buoyIds',
          label: this.t.tr('mooring.buoy'),
          type: filterTypes.CHECKBOX,
          originalData: buoys,
          options: buoys,
          query: 'buoyIds',
          values: {},
        },
        cageIds: {
          name: 'cageIds',
          label: this.t.tr('mooring.floater'),
          type: filterTypes.CHECKBOX,
          originalData: cages,
          options: cages,
          query: 'cageIds',
          values: {},
        },
      } satisfies Filters,
      defaultFilters: {
        buoyIds: true,
        cageIds: true,
      },
    });
  }

  protected onInputChange(index: number, name: (typeof tableColumns)[number]['field']) {
    const line = this.entries[index];

    if (name === 'CalculatedLength') {
      this.setComponentsQuantity(line.Components, line);
    }

    if (name === 'IsAccident' || name === 'DimForce') {
      calculateFactorsMbl(line.DimForce, line, this.materialFactors, line.IsAccident);
      this.calculateUtilizationFactor(line.Components, line);
    }
  }

  protected async calculateUtilizationFactor(component: MooringComponent[], bridle: MooringBridle) {
    const calculateUtilizationFactorDtos: CalculateUtilizationFactorDto[] = [];
    component.forEach((component) => {
      calculateUtilizationFactorDtos.push({
        Id: component.Id,
        HasKnot: component.HasKnot,
        MblRopes: bridle.MblRopes,
        MblSinkRopes: bridle.MblSinkRopes,
        MblStraps: bridle.MblStraps,
        MblChainShackleConnectionPlates: bridle.MblChainShackleConnectionPlates,
        MblUsedChainRopeWithKnot: bridle.MblUsedChainRopeWithKnot,
      });
    });

    const response = await this.mooringService.calculateUtilizationFactor({
      mooringId: this.mooringId,
      mooringStep: MooringStep.BRIDLE,
      dto: calculateUtilizationFactorDtos,
    });

    response?.data.forEach((element) => {
      component.find((x) => x.Id === element.Id).UtilizationFactor = element.UtilizationFactor;
    });
  }

  protected openCageBridles() {
    const cages = this.entries.reduce((acc, curr) => {
      if (!curr.CageId) return acc;

      let c = acc[curr.CageId] as BridleCage | undefined;

      if (!c) {
        c = {
          write: true,
          CageMooringNo: curr.CageNo,
          hasMultipleDimForce: false,
          hasMultipleMblChainShackle: false,
          hasMultipleMblStraps: false,
          hasMultipleMblRopes: false,
          hasMultipleMblUseChainRope: false,
          hasMultipleMblSinkRopes: false,
          DimForce: 0,
          MblRopes: 0,
          MblSinkRopes: 0,
          MblStraps: 0,
          MblChainShackleConnectionPlates: 0,
          MblUsedChainRopeWithKnot: 0,
        };
        acc[curr.CageId] = c;
      }

      // console.log(`cage ${curr.CageId}`, c.dimForce, curr.DimForce);
      if (c.DimForce !== 0 && c.DimForce !== curr.DimForce) {
        c.hasMultipleDimForce = true;
      }
      if ((c.MblRopes !== 0 && c.MblRopes !== curr.MblRopes) || 0) {
        c.hasMultipleMblRopes = true;
      }

      if ((c.MblStraps !== 0 && c.MblStraps !== curr.MblStraps) || 0) {
        c.hasMultipleMblStraps = true;
      }

      if ((c.MblChainShackleConnectionPlates !== 0 && c.MblChainShackleConnectionPlates !== curr.MblChainShackleConnectionPlates) || 0) {
        c.hasMultipleMblChainShackle = true;
      }
      if ((c.MblUsedChainRopeWithKnot !== 0 && c.MblUsedChainRopeWithKnot !== curr.MblUsedChainRopeWithKnot) || 0) {
        c.hasMultipleMblUseChainRope = true;
      }

      if ((c.MblSinkRopes !== 0 && c.MblSinkRopes !== curr.MblSinkRopes) || 0) {
        c.hasMultipleMblSinkRopes = true;
      }

      c.DimForce = curr.DimForce || 0;
      c.MblStraps = curr.MblStraps || 0;
      c.MblRopes = curr.MblRopes || 0;
      c.MblSinkRopes = curr.MblSinkRopes || 0;
      c.MblChainShackleConnectionPlates = curr.MblChainShackleConnectionPlates || 0;
      c.MblUsedChainRopeWithKnot = curr.MblUsedChainRopeWithKnot || 0;

      return acc;
    }, {});

    this.dialogService
      .open({
        viewModel: BridleCageDialog,
        position: () => 0,
        model: {
          bridleCages: Object.values(cages),
        } satisfies BridleCageDialogArgs,
      })
      .whenClosed(({ output }: { output?: BridleCageDialogResult[] }) => {
        if (!output?.length) return;

        for (const entry of output) {
          for (const line of this.entries) {
            if (line.CageNo !== entry.CageMooringNo) continue;
            if (line) {
              line.DimForce = entry.DimForce;
              line.MblRopes = entry.MblRopes;
              line.MblSinkRopes = entry.MblSinkRopes;
              line.MblStraps = entry.MblStraps;
              line.MblChainShackleConnectionPlates = entry.MblChainShackleConnectionPlates;
              line.MblUsedChainRopeWithKnot = entry.MblUsedChainRopeWithKnot;
            }
          }
        }

        this.onSave({
          entries: this.entries,
          canNavigateOnSave: false,
        });
      });
  }

  protected onElementSelected(type: MooringComponentType, item: { id: number; title: string }, line: MooringBridle, entry: keyof MooringBridle) {
    if (type == 'buoy' && entry === 'BuoyId') {
      line.BuoyId = item.id;
      line.BuoyNo = item.title;
    }

    if (type == 'cage' && entry === 'CageId') {
      line.CageId = item.id;
      line.CageNo = item.title;
    }
  }

  protected openBridleCalculator() {
    void this.dialogService
      .open({
        viewModel: MooringBridleCalculatorDialog,
        position: () => 0,
        model: {
          mooringId: this.mooringId,
        } satisfies MooringBridleCalculatorDialogArgs,
      })
      .whenClosed(({ output }: { output?: MooringBridleCalculatorDialogResult }) => {
        if (!output?.bridles.length) return;

        for (const bridle of output.bridles) {
          const existing = this.entries.find((e) => e.Id == bridle.id);
          if (!existing) continue;

          existing.CalculatedLength = bridle.length;
        }
      });
  }

  protected override postPasteAdjustment(index: number, name: (typeof tableColumns)[number]['field']) {
    this.onInputChange(index, name);
  }

  protected setComponentQuantity(component: MooringComponent, bridle: MooringBridle) {
    const strapLengths = bridle.Components.reduce((acc, curr) => {
      if (isStrapArticleType(curr.TypeVariant)) {
        acc += curr.PrimaryDimension || 0;
      }
      return acc;
    }, 0);

    if (component.CategoryType === MooringCategoryType.ROPE) {
      component.Quantity = ensureNumberOrDefault(bridle.CalculatedLength - strapLengths, 1);
    }
  }

  protected setComponentsQuantity(components: MooringComponent[], bridle: MooringBridle) {
    const strapLengths = bridle.Components.reduce((acc, curr) => {
      if (isStrapArticleType(curr.TypeVariant)) {
        acc += curr.PrimaryDimension || 0;
      }
      return acc;
    }, 0);

    components.forEach((component) => {
      if (component.CategoryType === MooringCategoryType.ROPE) {
        component.Quantity = ensureNumberOrDefault(bridle.CalculatedLength - strapLengths, 1);
      }
    });
  }

  protected onArticleSelected(searchResult: MooringComponentSearchResult, component: MooringComponent, bridle: MooringBridle) {
    const res = searchResult.return;
    if (!res) return;
    component.ArticleProductId = res.Id;
    component.MBL = res.MBL;
    component.ProductName = res.ProductName;
    component.CategoryName = res.Category;
    component.TypeName = res.Type;
    component.ProductTypeName = res.ProductType;
    component.PrimaryDimension = res.PrimaryDimension;
    component.SecondaryDimension = res.SecondaryDimension;
    component.CategoryType = res.CategoryType;
    component.TypeVariant = res.TypeVariant;

    this.setComponentQuantity(component, bridle);

    this.onSave({
      entries: this.entries,
      canNavigateOnSave: false,
    });
  }

  protected getEntriesForPopulate(): MooringSelectEntriesForPupulateArgs {
    const entries = structuredClone(this.entries);

    console.log(entries);
    const groups = entries
      // sort by CageNo then by BuoyNo
      .sort((a, b) => {
        if (a.CageNo < b.CageNo) return -1;
        if (a.CageNo > b.CageNo) return 1;
        if (a.BuoyNo < b.BuoyNo) return -1;
        if (a.BuoyNo > b.BuoyNo) return 1;
        return 0;
      })
      .reduce(
        (acc, curr) => {
          const identifier = curr.CageId || '-1';

          if (!acc[identifier]) {
            acc[identifier] = {
              _identifier: identifier,
              title: `${this.t.tr('mooring.floater')}: ${curr.CageNo}`,
              collapsed: true,
              options: [],
            };
          }

          acc[identifier].options.push({
            id: curr.Id,
            name: `${this.t.tr('mooring.bridleNo')}: ${curr.AnalysisNo ?? ''}\n${this.t.tr('mooring.buoy')}: ${curr.BuoyNo}`,
            selected: false,
            articles: curr.Components?.length || 0,
          } satisfies SelectionOption);

          return acc;
        },
        {} as { [key: string]: MooringSelectEntriesForPupulateArgs['groups'][number] }
      );

    return {
      groups: Object.values(groups)?.sort((a, b) => {
        return a.title.localeCompare(b.title, undefined, { usage: 'sort', numeric: true });
      }),
    };
  }

  protected onAddComponent() {
    this.entries.push({
      AnalysisNo: ``,
    } as MooringBridle);
  }

  @AsyncErrorHandler
  protected async onDelete(index: number) {
    const res = await this.confirmation.confirmDelete();
    if (!res) return;

    const entry = this.entries[index];
    if (!entry) return;

    if (entry.Id) {
      await this.mooringService.deleteBridle(this.mooringId, entry.Id);
    }

    this.entries.splice(index, 1);
    this.toast.showDeleted();
  }

  protected save() {
    this.onSave({
      entries: this.entries,
      canNavigateOnSave: false,
    });
  }

  protected addEntryComment(component: MooringBridle) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.Bridle,
    });
  }

  protected addComponentComment(component: MooringComponent) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.BridleComponent,
    });
  }

  protected async clearForm() {
    const approved = await this.confirmation.confirmYesCancel('mooring.clearForm', 'mooring.clearFormConfirmation');

    if (approved) {
      const restedTable = this.resetFields(this.entries, fieldsToReset);
      this.entries = restedTable;
    }
  }

  protected resetFields<T extends Record<string, any>>(mooringBridles: T[], fieldsToReset: (keyof T)[]): T[] {
    return mooringBridles.map((mooringBridle) => {
      const updatedMooringBridle = { ...mooringBridle };

      fieldsToReset.forEach((field) => {
        const value = updatedMooringBridle[field];

        if (field === 'CalculatedLength') {
          (updatedMooringBridle[field] as number) = 0;
        } else if (typeof value === 'boolean') {
          (updatedMooringBridle[field] as boolean) = false;
        } else {
          updatedMooringBridle[field] = null;
        }
      });

      return updatedMooringBridle;
    });
  }

  protected unbind() {
    this.pubsub.unsub();
    super.unbind();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}

const fieldsToReset: (keyof MooringBridle)[] = [
  'CalculatedLength',
  'DimForce',
  'IsAccident',
  'MblRopes',
  'MblSinkRopes',
  'MblStraps',
  'MblChainShackleConnectionPlates',
  'MblUsedChainRopeWithKnot',
];

const tableColumns: ListFieldTyped<MooringBridle>[] = [
  {
    field: 'BuoyNo',
    title: 'mooring.buoy',
    selected: true,
    visible: true,
    filterType: 'TEXT',
  },
  {
    field: 'CageNo',
    title: 'mooring.floater',
    selected: true,
    visible: true,
    filterType: 'TEXT',
  },
  {
    field: 'AnalysisNo',
    title: 'mooring.analysisNo',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: (value: string) => value,
    inputType: 'text',
  },
  {
    field: 'CalculatedLength',
    title: 'mooring.theoreticalLength',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'float',
  },
  {
    field: 'DimForce',
    title: 'mooring.dimForce',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'IsAccident',
    title: 'mooring.isAccident',
    helpText: 'mooring.dimForceDescription',
    selected: true,
    visible: true,
    filterType: 'BOOL',
    transform: toChecked,
    inputType: 'bool',
  },
  {
    field: 'MblRopes',
    title: 'mooring.minMblRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblSinkRopes',
    title: 'mooring.minMblSinkRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblStraps',
    title: 'mooring.minMblStrap',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblChainShackleConnectionPlates',
    title: 'mooring.minMblChain',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblUsedChainRopeWithKnot',
    title: 'mooring.minMblUsedChainRopeWithKnot',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'actions',
    title: '',
    selected: true,
    visible: true,
    filterType: 'none',
  },
];
